import { useCallback, useMemo } from 'react';

export function useSelectOptions<T>({
  value,
  items,
  getDisplayTextOfItem,
  getKeyOfItem
}: UseSelectOptionsOptions<T>): UseSelectOptionsReturnValue<T> {
  const createOptionForItem = useCallback(
    (item: T, index: number): SelectOption<T> => {
      return {
        item,
        key: getKeyOfItem(item, index),
        displayText: getDisplayTextOfItem(item)
      };
    },
    [getKeyOfItem, getDisplayTextOfItem]
  );

  const options = useMemo(() => {
    return items.map<SelectOption<T>>(createOptionForItem);
  }, [items, createOptionForItem]);

  const currentOption = useMemo<SelectOption<T>>(() => {
    let option = options.find((o) => o.item === value);

    if (!option) {
      option = createOptionForItem(value, -1);
    }

    return option;
  }, [options, value, createOptionForItem]);

  return { options, currentOption };
}

export function getKeyOfSelectOption(option: SelectOption<unknown>): string {
  return option.key;
}

export function getDisplayTextOfSelectOption(
  option: SelectOption<unknown>
): string {
  return option.displayText;
}

export type UseSelectOptionsOptions<T> = {
  value: T;
  items: Array<T>;
  getKeyOfItem: (item: T, index: number) => string;
  getDisplayTextOfItem: (item: T) => string;
};

export type UseSelectOptionsReturnValue<T> = {
  options: Array<SelectOption<T>>;
  currentOption: SelectOption<T>;
};

export type SelectOption<T> = {
  key: string;
  displayText: string;
  item: T;
};
